import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getBookingOfferBreakdown, sumOfferBreakdowns } from '../utils/bookingOfferBreakdown';
import './Payments.css';

function Payments() {
  const { salonId } = useParams();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [groupedBookings, setGroupedBookings] = useState({});
  const [expandedDates, setExpandedDates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [salonInfo, setSalonInfo] = useState(null);

  const fetchSalonInfo = useCallback(async () => {
    try {
      const salonDoc = await getDocs(query(collection(db, 'salons'), where('__name__', '==', salonId)));
      if (!salonDoc.empty) {
        const data = salonDoc.docs[0].data();
        setSalonInfo({
          salonName: data.profile?.shopName || 'N/A',
          ownerName: data.profile?.ownerName || 'N/A',
          contactNumber: data.phoneNumber || 'N/A'
        });
      }
    } catch (err) {
    }
  }, [salonId]);

  const fetchCompletedBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch completed bookings for this salon
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('salonId', '==', salonId),
        where('bookingStatus', '==', 'completed'),
        orderBy('bookingDate', 'desc')
      );

      const querySnapshot = await getDocs(bookingsQuery);
      const bookingsData = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        const originalPrice = data.originalPrice || data.servicePrice || data.totalPrice || 0;
        const totalPrice = data.totalPrice || 0;
        const offerBr = getBookingOfferBreakdown({ ...data, originalPrice, totalPrice });

        bookingsData.push({
          id: doc.id,
          bookingReference: data.bookingReference,
          customerName: data.customerName,
          customerPhone: data.customerPhone,
          serviceName: data.serviceName,
          bookingDate: data.bookingDate,
          bookingTime: data.bookingTime,
          originalPrice: originalPrice,
          totalPrice: totalPrice,
          discountAmount: offerBr.totalDiscountAmount,
          salonOfferAmount: offerBr.salonOfferAmount,
          ulloorOfferAmount: offerBr.ulloorOfferAmount,
          otherDiscountAmount: offerBr.otherDiscountAmount,
          totalDiscountAmount: offerBr.totalDiscountAmount,
          offerApplied: data.offerApplied || false,
          offerType: data.offerType || 'none',
          paymentMode: data.paymentMode || 'N/A',
          createdAt: data.createdAt
        });
      });

      setBookings(bookingsData);
      groupBookingsByDate(bookingsData);
    } catch (err) {
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  useEffect(() => {
    fetchSalonInfo();
    fetchCompletedBookings();
  }, [fetchSalonInfo, fetchCompletedBookings]);

  const groupBookingsByDate = (bookingsData) => {
    const grouped = {};

    bookingsData.forEach((booking) => {
      const date = new Date(booking.bookingDate).toLocaleDateString('en-IN', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });

      if (!grouped[date]) {
        grouped[date] = {
          bookings: [],
          totalOfferAmount: 0,
          totalSalonOfferAmount: 0,
          totalUlloorOfferAmount: 0,
          totalOtherOfferAmount: 0,
          rawDate: booking.bookingDate
        };
      }

      grouped[date].bookings.push(booking);
      grouped[date].totalOfferAmount += booking.totalDiscountAmount;
      grouped[date].totalSalonOfferAmount += booking.salonOfferAmount;
      grouped[date].totalUlloorOfferAmount += booking.ulloorOfferAmount;
      grouped[date].totalOtherOfferAmount += booking.otherDiscountAmount;
    });

    setGroupedBookings(grouped);
  };

  const toggleDateExpansion = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const handleRefresh = () => {
    fetchCompletedBookings();
  };

  const handleBack = () => {
    navigate('/salons');
  };

  const downloadDayReport = (date, bookings, dayOfferTotals) => {
    const t =
      typeof dayOfferTotals === 'number'
        ? {
            totalDiscountAmount: dayOfferTotals,
            totalSalonOfferAmount: 0,
            totalUlloorOfferAmount: 0,
            totalOtherOfferAmount: 0
          }
        : dayOfferTotals;
    const totalOfferAmount = t.totalDiscountAmount ?? 0;
    const salonDay = t.totalSalonOfferAmount ?? 0;
    const ulloorDay = t.totalUlloorOfferAmount ?? 0;
    const otherDay = t.totalOtherOfferAmount ?? 0;
    const doc = new jsPDF();
    
    // Set up the document
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let yPosition = 20;

    // Add header
    doc.setFontSize(20);
    doc.setTextColor(102, 126, 234);
    doc.text('Ulloor Styles - Payment Report', pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 10;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, pageWidth / 2, yPosition, { align: 'center' });
    
    // Add salon information
    yPosition += 15;
    doc.setFontSize(12);
    doc.setTextColor(40, 40, 40);
    if (salonInfo) {
      doc.text(`Salon: ${salonInfo.salonName}`, 14, yPosition);
      yPosition += 7;
      doc.text(`Owner: ${salonInfo.ownerName}`, 14, yPosition);
      yPosition += 7;
      doc.text(`Contact: ${salonInfo.contactNumber}`, 14, yPosition);
      yPosition += 7;
    }
    
    // Add date and summary
    yPosition += 5;
    doc.setFontSize(14);
    doc.setTextColor(102, 126, 234);
    doc.text(`Report Date: ${date}`, 14, yPosition);
    
    yPosition += 10;
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text(`Total Bookings: ${bookings.length}`, 14, yPosition);
    yPosition += 7;
    doc.text(`Salon offers (savings): Rs. ${salonDay.toFixed(2)}`, 14, yPosition);
    yPosition += 7;
    doc.text(`Ulloor Styles offers (savings): Rs. ${ulloorDay.toFixed(2)}`, 14, yPosition);
    yPosition += 7;
    if (otherDay > 0.5) {
      doc.text(`Other / legacy offers: Rs. ${otherDay.toFixed(2)}`, 14, yPosition);
      yPosition += 7;
    }
    doc.text(`Total discounts: Rs. ${totalOfferAmount.toFixed(2)}`, 14, yPosition);
    yPosition += 7;
    doc.text(
      `Received From Customer: Rs. ${bookings.reduce((sum, b) => sum + b.totalPrice, 0).toFixed(2)}`,
      14,
      yPosition
    );

    // Add bookings table
    yPosition += 10;

    const tableData = bookings.map((booking) => [
      booking.bookingReference || '—',
      booking.bookingTime,
      booking.customerName,
      booking.serviceName,
      `Rs. ${booking.originalPrice.toFixed(2)}`,
      booking.salonOfferAmount > 0 ? `Rs. ${booking.salonOfferAmount.toFixed(2)}` : '—',
      booking.ulloorOfferAmount > 0 ? `Rs. ${booking.ulloorOfferAmount.toFixed(2)}` : '—',
      booking.otherDiscountAmount > 0 ? `Rs. ${booking.otherDiscountAmount.toFixed(2)}` : '—',
      `Rs. ${booking.totalPrice.toFixed(2)}`,
      booking.paymentMode || '—'
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [
        [
          'Booking ID',
          'Time',
          'Customer',
          'Service',
          'Original',
          'Salon off.',
          'Ulloor off.',
          'Other',
          'Total paid',
          'Pay'
        ]
      ],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [102, 126, 234],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [40, 40, 40]
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251]
      },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 12 },
        2: { cellWidth: 24 },
        3: { cellWidth: 28 },
        4: { cellWidth: 18 },
        5: { cellWidth: 15 },
        6: { cellWidth: 15 },
        7: { cellWidth: 14 },
        8: { cellWidth: 18 },
        9: { cellWidth: 16 }
      },
      margin: { top: 10, left: 14, right: 14 },
      didDrawPage: (data) => {
        // Add footer
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }
    });

    // Add totals at the bottom
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.setFont(undefined, 'bold');
    
    const received = bookings.reduce((sum, b) => sum + b.totalPrice, 0);
    const totals = [
      ['Total original (list) amount:', `Rs. ${bookings.reduce((sum, b) => sum + b.originalPrice, 0).toFixed(2)}`],
      ['Total salon offers:', `Rs. ${salonDay.toFixed(2)}`],
      ['Total Ulloor Styles offers:', `Rs. ${ulloorDay.toFixed(2)}`],
      ...(otherDay > 0.5 ? [['Other / legacy offers:', `Rs. ${otherDay.toFixed(2)}`]] : []),
      ['Total discounts (all):', `Rs. ${totalOfferAmount.toFixed(2)}`],
      ['Received from customer:', `Rs. ${received.toFixed(2)}`]
    ];

    autoTable(doc, {
      startY: finalY,
      body: totals,
      theme: 'plain',
      styles: {
        fontSize: 10,
        fontStyle: 'bold',
        textColor: [40, 40, 40]
      },
      columnStyles: {
        0: { cellWidth: 140, halign: 'right' },
        1: { cellWidth: 45, halign: 'right', textColor: [5, 150, 105] }
      },
      margin: { left: 14, right: 14 }
    });

    // Save the PDF
    const fileName = `Payment_Report_${salonInfo?.salonName || 'Salon'}_${date.replace(/[,\s]/g, '_')}.pdf`;
    doc.save(fileName);
  };

  const allOffers = useMemo(() => sumOfferBreakdowns(bookings), [bookings]);

  if (error) {
    return (
      <div className="payments-page">
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={handleRefresh} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payments-page">
      <div className="page-header">
        <button onClick={handleBack} className="back-btn">
          ← Back to Salons
        </button>
        <div className="header-info">
          <h2>Payments Report</h2>
          {salonInfo && (
            <div className="salon-details">
              <p><strong>Salon:</strong> {salonInfo.salonName}</p>
              <p><strong>Owner:</strong> {salonInfo.ownerName}</p>
              <p><strong>Contact:</strong> {salonInfo.contactNumber}</p>
            </div>
          )}
        </div>
        <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="stats-summary">
        <div className="stat-card">
          <h3>Total Completed Bookings</h3>
          <p className="stat-number">{bookings.length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <p className="stat-number">
            ₹{bookings.reduce((sum, booking) => sum + booking.totalPrice, 0).toFixed(2)}
          </p>
        </div>
        <div className="stat-card stat-card-offers">
          <h3>Salon offers (total savings)</h3>
          <p className="stat-number stat-offer-salon">₹{allOffers.salonOfferAmount.toFixed(2)}</p>
        </div>
        <div className="stat-card stat-card-offers">
          <h3>Ulloor Styles offers (total savings)</h3>
          <p className="stat-number stat-offer-ulloor">₹{allOffers.ulloorOfferAmount.toFixed(2)}</p>
        </div>
        <div className="stat-card">
          <h3>Total discounts (all)</h3>
          <p className="stat-number">
            ₹{allOffers.totalDiscountAmount.toFixed(2)}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading bookings...</p>
        </div>
      ) : (
        <div className="bookings-grouped-container">
          {Object.keys(groupedBookings).length === 0 ? (
            <div className="empty-state">
              <p>No completed bookings found for this salon.</p>
            </div>
          ) : (
            Object.entries(groupedBookings)
              .sort((a, b) => new Date(b[1].rawDate) - new Date(a[1].rawDate))
              .map(([date, data]) => (
                <div key={date} className="date-group">
                  <div className="date-header">
                    <div 
                      className="date-header-main"
                      onClick={() => toggleDateExpansion(date)}
                    >
                      <div className="date-header-left">
                        <span className="expand-icon">
                          {expandedDates[date] ? '▼' : '▶'}
                        </span>
                        <h3>{date}</h3>
                        <span className="booking-count">
                          (Completed {data.bookings.length} booking{data.bookings.length !== 1 ? 's' : ''})
                        </span>
                      </div>
                      <div className="date-header-right date-header-offers">
                        <span className="offer-split-line">
                          <span className="offer-split-item">Salon: <strong>₹{data.totalSalonOfferAmount.toFixed(2)}</strong></span>
                          <span className="offer-split-sep">·</span>
                          <span className="offer-split-item">Ulloor: <strong>₹{data.totalUlloorOfferAmount.toFixed(2)}</strong></span>
                          {data.totalOtherOfferAmount > 0.5 && (
                            <>
                              <span className="offer-split-sep">·</span>
                              <span className="offer-split-item">Other: <strong>₹{data.totalOtherOfferAmount.toFixed(2)}</strong></span>
                            </>
                          )}
                          <span className="offer-split-sep">·</span>
                          <span className="offer-split-item total-discount-label">Total: <strong>₹{data.totalOfferAmount.toFixed(2)}</strong></span>
                        </span>
                      </div>
                    </div>
                    <button
                      className="download-pdf-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadDayReport(date, data.bookings, {
                          totalDiscountAmount: data.totalOfferAmount,
                          totalSalonOfferAmount: data.totalSalonOfferAmount,
                          totalUlloorOfferAmount: data.totalUlloorOfferAmount,
                          totalOtherOfferAmount: data.totalOtherOfferAmount
                        });
                      }}
                      title="Download PDF Report"
                    >
                      📄 Download PDF
                    </button>
                  </div>

                  {expandedDates[date] && (
                    <div className="bookings-list">
                      {data.bookings.map((booking) => (
                        <div key={booking.id} className="booking-card">
                          <div className="booking-header">
                            <div className="booking-id-section">
                              <span className="booking-label">Booking ID:</span>
                              <span className="booking-reference">{booking.bookingReference}</span>
                            </div>
                            <div className="booking-time">
                              {booking.bookingTime}
                            </div>
                          </div>

                          <div className="booking-details">
                            <div className="detail-row">
                              <span className="detail-label">Customer:</span>
                              <span className="detail-value">{booking.customerName}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Phone:</span>
                              <span className="detail-value">{booking.customerPhone}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Service:</span>
                              <span className="detail-value">{booking.serviceName}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Payment Mode:</span>
                              <span className="detail-value">{booking.paymentMode}</span>
                            </div>
                          </div>

                          <div className="pricing-details">
                            <div className="price-row">
                              <span className="price-label">Original Price:</span>
                              <span className="price-value">₹{booking.originalPrice.toFixed(2)}</span>
                            </div>
                            {booking.salonOfferAmount > 0 && (
                              <div className="price-row discount-row">
                                <span className="price-label">Salon offer (savings)</span>
                                <span className="price-value discount-value">
                                  −₹{booking.salonOfferAmount.toFixed(2)}
                                </span>
                              </div>
                            )}
                            {booking.ulloorOfferAmount > 0 && (
                              <div className="price-row discount-row discount-row-ulloor">
                                <span className="price-label">Ulloor Styles offer (savings)</span>
                                <span className="price-value discount-value">
                                  −₹{booking.ulloorOfferAmount.toFixed(2)}
                                </span>
                              </div>
                            )}
                            {booking.otherDiscountAmount > 0 && (
                              <div className="price-row discount-row">
                                <span className="price-label">Other / legacy offers</span>
                                <span className="price-value discount-value">
                                  −₹{booking.otherDiscountAmount.toFixed(2)}
                                </span>
                              </div>
                            )}
                            {booking.offerApplied && booking.salonOfferAmount === 0 && booking.ulloorOfferAmount === 0 && booking.otherDiscountAmount === 0 && (
                              <div className="offer-badge">
                                <span className="offer-icon">🎉</span>
                                <span>Offer ({booking.offerType === 'full' ? '100%' : booking.offerType === 'half' ? '50%' : booking.offerType === 'weekend' ? 'Weekend' : 'Applied'})</span>
                              </div>
                            )}
                            <div className="price-row total-row">
                              <span className="price-label">Total Price:</span>
                              <span className="price-value total-value">₹{booking.totalPrice.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
          )}
        </div>
      )}
    </div>
  );
}

export default Payments;

